import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiLoader, FiPlus, FiSearch, FiUsers, FiX } from 'react-icons/fi';
import {
  cChannels, cCreateChannel, cDeleteChannel, cMyChannels, cMySubscriptions, cSubscribeChannel,
  cUpload, fullUrl,
} from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import ChannelCard from '../../components/community/ChannelCard';
import { BLUE, BLUE_GRADIENT } from '../../components/community/brand';

/**
 * Channels — **a member may create up to 3 channels** and other members
 * subscribe (“ជាវ”) to them. Three tabs: my channels, the ones I subscribed to
 * and the public directory (search + categories).
 */
const EMPTY = { name: '', nickname: '', about: '', category: '', is_public: true, is_open: true };

export default function ChannelsPage() {
  const { t } = useLanguage();
  const { user, requireAuth, openAuth, refreshChannels } = useCommunity();
  const [tab, setTab] = useState('mine');           // mine | subs | discover
  const [mine, setMine] = useState({ items: [], limit: 3, remaining: 3, can_create: false });
  const [subs, setSubs] = useState([]);
  const [found, setFound] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);

  /** Upload the channel profile picture / cover (they are saved on create). */
  const pick = async (e, field, setFlag) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFlag(true);
    try {
      const res = await cUpload([file]);
      setForm((f) => ({ ...f, [field]: res.urls?.[0] || '' }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setFlag(false);
    }
  };
  const pickAvatar = (e) => pick(e, 'avatar', setAvatarBusy);
  const pickCover = (e) => pick(e, 'cover', setCoverBusy);

  const loadMine = () => (user
    ? cMyChannels().then(setMine).catch(() => {})
    : Promise.resolve(setMine({ items: [], limit: 3, remaining: 0, can_create: false })));
  const loadSubs = () => (user
    ? cMySubscriptions().then((r) => setSubs(r.items || [])).catch(() => {})
    : Promise.resolve());
  const loadDiscover = (term = '') => cChannels({ q: term || undefined, limit: 24 })
    .then((r) => setFound(r.items || []))
    .catch(() => setFound([]));

  useEffect(() => {
    setLoading(true);
    Promise.all([loadMine(), loadSubs(), loadDiscover('')]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const create = async (e) => {
    e.preventDefault();
    if (!requireAuth(t('loginRequiredDesc'))) return;
    if (!form.name.trim()) { toast.error(t('channelName')); return; }
    setBusy(true);
    try {
      const res = await cCreateChannel({
        name: form.name.trim(),
        nickname: form.nickname.trim(),
        about: form.about.trim(),
        category: form.category.trim(),
        avatar: form.avatar || '',
        cover: form.cover || '',
        is_public: form.is_public,
        is_open: form.is_open,
      });
      toast.success(`${t('channelCreate')} ✓ · ${t('channelRemaining').replace('{n}', res.remaining)}`);
      setForm(EMPTY);
      setCreating(false);
      await Promise.all([loadMine(), loadDiscover()]);
      refreshChannels();          // the account switcher shows the new channel
      setTab('mine');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  const subscribe = async (channel) => {
    if (!requireAuth(t('loginRequiredDesc'))) return;
    try {
      const res = await cSubscribeChannel(channel.slug || channel.id);
      toast.success(res.subscribed ? t('subscribed') : res.pending ? t('subscriberPending') : t('done'));
      await Promise.all([loadMine(), loadSubs(), loadDiscover(q)]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const remove = async (channel) => {
    if (!window.confirm(t('channelDeleteConfirm'))) return;
    try {
      await cDeleteChannel(channel.slug || channel.id);
      toast.success(t('deletedOk'));
      await Promise.all([loadMine(), loadSubs(), loadDiscover(q)]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const input = 'w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 text-sm outline-none focus:border-gray-400';

  const tabBtn = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      className={`px-4 py-2 rounded-full text-xs sm:text-sm font-bold ${tab === key ? 'text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
      style={tab === key ? { background: BLUE } : undefined}
    >
      {label}
    </button>
  );

  const list = (items, ownerView) => (items.length === 0
    ? <p className="text-sm text-gray-400">{t('channelEmpty')}</p>
    : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((ch) => (
          <ChannelCard
            key={ch.id}
            channel={ch}
            ownerView={ownerView}
            onSubscribe={subscribe}
            onDelete={remove}
          />
        ))}
      </div>
    ));

  return (
    <div className="mx-auto max-w-[1200px] px-3 lg:px-4 py-4">
      <header className="flex items-center gap-3 flex-wrap mb-2">
        <h1 className="text-lg sm:text-xl font-extrabold">{t('channelsTitle')}</h1>
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
          {t('channelLimit')}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {tab === 'discover' && (
            <div className="relative">
              <FiSearch className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); loadDiscover(e.target.value); }}
                placeholder={t('channelsDiscover')}
                className="pl-9 pr-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 text-sm w-40 sm:w-56"
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => (user ? setCreating((v) => !v) : openAuth('signin', t('loginRequiredDesc')))}
            disabled={!!user && !mine.can_create}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-white text-xs sm:text-sm font-bold disabled:opacity-50"
            style={{ background: BLUE }}
          >
            <FiPlus className="w-4 h-4" /> {t('channelCreate')}
          </button>
        </div>
      </header>

      {/* how many channels are still free (3 per member) */}
      {user && (
        <p className="text-xs text-gray-500 mb-3">
          {mine.remaining > 0
            ? t('channelRemaining').replace('{n}', mine.remaining)
            : t('channelLimit')}
        </p>
      )}

      {creating && (
        <form onSubmit={create} className="mb-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 space-y-2.5 anim-pop">
          <div className="flex items-center justify-between">
            <h2 className="font-extrabold text-sm">{t('channelCreate')}</h2>
            <button type="button" onClick={() => setCreating(false)} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label={t('close')}>
              <FiX className="w-4 h-4" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t('channelName')} className={input} />
            <input value={form.nickname} onChange={(e) => setForm((f) => ({ ...f, nickname: e.target.value }))} placeholder={t('channelNickname')} className={input} />
          </div>
          <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} placeholder={t('channelCategory')} className={input} />
          <textarea value={form.about} onChange={(e) => setForm((f) => ({ ...f, about: e.target.value }))} rows={2} placeholder={t('channelAbout')} className={input} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <span className="block text-[11px] font-bold text-gray-500 mb-1">{t('channelProfile')}</span>
              <div className="flex items-center gap-2">
                {form.avatar ? (
                  <img src={fullUrl(form.avatar)} alt="" className="w-12 h-12 rounded-xl object-cover" />
                ) : (
                  <span className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800" />
                )}
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-xs font-bold text-gray-600 dark:text-gray-300 cursor-pointer">
                  <input type="file" accept="image/*" hidden onChange={pickAvatar} />
                  {avatarBusy && <FiLoader className="w-4 h-4 animate-spin" />}
                  {t('channelUploadProfile')}
                </label>
              </div>
            </div>
            <div>
              <span className="block text-[11px] font-bold text-gray-500 mb-1">{t('channelCover')}</span>
              {form.cover ? (
                <img src={fullUrl(form.cover)} alt="" className="w-full h-20 rounded-xl object-cover mb-1.5" />
              ) : (
                <span className="block w-full h-20 rounded-xl mb-1.5" style={{ background: BLUE_GRADIENT }} />
              )}
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-xs font-bold text-gray-600 dark:text-gray-300 cursor-pointer">
                <input type="file" accept="image/*" hidden onChange={pickCover} />
                {coverBusy && <FiLoader className="w-4 h-4 animate-spin" />}
                {t('channelUploadCover')}
              </label>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-gray-600 dark:text-gray-300">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={form.is_public} onChange={(e) => setForm((f) => ({ ...f, is_public: e.target.checked }))} />
              {t('channelPublic')} — {t('channelPublicHint')}
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={form.is_open} onChange={(e) => setForm((f) => ({ ...f, is_open: e.target.checked }))} />
              {t('channelOpen')}
            </label>
          </div>
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-white text-sm font-bold disabled:opacity-60" style={{ background: BLUE }}>
            {busy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlus className="w-4 h-4" />} {t('channelCreate')}
          </button>
        </form>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {tabBtn('mine', t('channelMyChannels'))}
        {tabBtn('subs', t('channelMySubscriptions'))}
        {tabBtn('discover', t('channelsDiscover'))}
      </div>

      {loading ? <p className="text-sm text-gray-400">{t('loading')}</p>
        : tab === 'mine' ? list(mine.items, true)
          : tab === 'subs' ? list(subs, false)
            : list(found, false)}

      <div className="mt-6 flex items-center gap-3 text-[11px] text-gray-400">
        <FiUsers className="w-4 h-4" />
        {t('channelLimit')}
        <Link to="/groups" className="ml-auto font-bold hover:underline" style={{ color: BLUE }}>
          {t('groupsNav')} →
        </Link>
      </div>
    </div>
  );
}
